import { twelveWeekExercises } from "./exercises";
import { ScheduleType, TwelveWeekProgramPhase } from "./types";

export const twelveWeekIntro = {
  title: "X3 Home Gym Workout Program: Intro and Best Practices",
  pageTitle: "Welcome to Your X3 12-Week Journey",
  subtitle:
    "Learn how X3 works, how to train effectively, what to expect, and set yourself up for success.",
  description:
    "Watch this intro video before beginning the X3 12-Week Program. Dr. Jaquish explains how X3 works and walks through key principles that will help transform your body.",
  videoId: "6ee3aaa28a94555b8cd90e78843d99e8",
  thumbnailTimestampSeconds: 346,
  transcriptPath: "/transcripts/12-week/intro.md",
};

export const twelveWeekProgramPhases: Record<string, TwelveWeekProgramPhase> = {
  "weeks-1-4": {
    title: "Weeks 1–4",
    pageTitle: "Weeks 1–4: Foundational Phase",
    subtitle: "Build a strong foundation with variable resistance training",
    description: `This phase introduces you to the core principles that make X3 so effective: training to complete fatigue, maintaining constant band tension, and performing slow, controlled reps.
    During weeks 1–4, you’ll focus on mastering form and consistency while developing strength and stability using lighter resistance than you’ll use later.
    \n\n
    Stick to one set per exercise, push to full fatigue at all ranges of motion, and prioritize quality over quantity.
    \n\n
    This foundational phase will help you establish the habits and neuromuscular control needed to safely progress to heavier resistance and more demanding movements in later phases.`,
    videoId: "cdeb39c1128fc26e8bf51987bc80e092",
    thumbnailTimestampSeconds: 175,
    scheduleType: ScheduleType.FourDays,
    variations: false,
    transcriptPath: "/transcripts/12-week/weeks-1-to-4.md",
  },
  "weeks-5-8": {
    title: "Weeks 5–8",
    pageTitle: "Weeks 5–8: Strength Phase",
    subtitle: "Master the principles of variable resistance training",
    description: `Now that you’ve built a solid foundation, it’s time to challenge your body with heavier resistance and more refined technique.
    \n\n
    In this phase, you’ll continue training to complete fatigue, with renewed focus on three key principles: maintaining constant tension, progressing through diminishing ranges, and prioritizing controlled movement.
    \n\n
    Expect to see new exercise variations introduced—like the pec crossover and split squat—which deepen muscle engagement and reinforce neuromuscular control. Stick with a single set per movement, train to failure, and aim to move with intent and precision.
    \n\n
    Mastery in this phase sets the stage for more advanced adaptations in Weeks 9–12.`,
    videoId: "5c866c325703ff050be009cd52795801",
    thumbnailTimestampSeconds: 88,
    scheduleType: ScheduleType.SixDays,
    variations: true,
    transcriptPath: "/transcripts/12-week/weeks-5-to-8.md",
  },
  "weeks-9-12": {
    title: "Weeks 9–12",
    pageTitle: "Weeks 9–12: Optimization Phase",
    subtitle: "Refine your technique and lock in long-term results",
    description: `In this final phase of the X3 12-Week Program, you’ll take everything you’ve learned and apply it with greater precision and intent. Your goal isn’t just to complete the movements—it’s to master them.
    \n\n
    Weeks 9–12 focus on advanced principles like breath control, bracing, and intentional range management to help you maximize output and maintain progress.
    \n\n
    You’ll revisit key variations like the split squat and pec crossover with deeper focus and heavier resistance, using what you’ve built so far to drive new adaptations.
    \n\n
    Continue training to complete fatigue, reinforce the core X3 principles, and develop the confidence to carry these habits into your long-term fitness journey.`,
    videoId: "6db48b2c131e656306c13d6aa0776b94",
    thumbnailTimestampSeconds: 131,
    scheduleType: ScheduleType.SixDays,
    variations: true,
    transcriptPath: "/transcripts/12-week/weeks-9-to-12.md",
  },
  "after-12-weeks": {
    title: "Progressing After Completing Your X3 12-Week Program",
    pageTitle: "After 12 Weeks: Next Steps",
    subtitle: "How to continue your progress",
    description: `You’ve completed the X3 12-Week Program—now it’s time to turn consistency into long-term progress.
    \n\n
    This video outlines how to stay on track: keep training to fatigue, continue applying core X3 principles, and avoid common pitfalls that stall results. You’ll also hear how to adapt your training mindset and maintain motivation well beyond your first 12 weeks.
    \n\n
    When you're ready, explore one of our next-level programs designed to match your evolving goals or consider restarting the X3 12-Week Program.`,
    videoId: "3e7eaedffc6013ef4abdc38ee4b21302",
    thumbnailTimestampSeconds: 6,
    transcriptPath: "/transcripts/12-week/after-12-weeks.md",
  },
};

export const getSchedule = (
  type: "4-days-a-week" | "6-days-a-week",
): { day: string; label: string }[] => {
  if (type === "4-days-a-week") {
    return [
      { day: "Day 1", label: "Push Day" },
      { day: "Day 2", label: "Pull Day" },
      { day: "Day 3", label: "Rest Day" },
      { day: "Day 4", label: "Push Day" },
      { day: "Day 5", label: "Pull Day" },
      { day: "Day 6", label: "Rest Day" },
      { day: "Day 7", label: "Rest Day" },
    ];
  }

  return [
    { day: "Day 1", label: "Push Day" },
    { day: "Day 2", label: "Pull Day" },
    { day: "Day 3", label: "Push Day" },
    { day: "Day 4", label: "Pull Day" },
    { day: "Day 5", label: "Push Day" },
    { day: "Day 6", label: "Pull Day" },
    { day: "Day 7", label: "Rest Day" },
  ];
};

export const twelveWeekWorkouts = {
  "push-day": {
    title: "X3 Push Day",
    description:
      "Focuses on the major upper-body and lower-body pushing movements to build strength, size, and power. You’ll target your chest, triceps, shoulders, and quads.",
    exercises: [
      { ...twelveWeekExercises["chest-press"], slug: "chest-press" },
      {
        ...twelveWeekExercises["pec-crossover"],
        slug: "pec-crossover",
        variation: true,
        showOnWeeks: ["weeks-5-8", "weeks-9-12"],
      },
      { ...twelveWeekExercises["tricep-press"], slug: "tricep-press" },
      { ...twelveWeekExercises["overhead-press"], slug: "overhead-press" },
      { ...twelveWeekExercises["front-squat"], slug: "front-squat" },
      {
        ...twelveWeekExercises["split-squat"],
        slug: "split-squat",
        variation: true,
        showOnWeeks: ["weeks-5-8", "weeks-9-12"],
      },
      {
        ...twelveWeekExercises["upright-row"],
        slug: "upright-row",
        variation: true,
        showOnWeeks: ["weeks-1-4", "weeks-5-8", "weeks-9-12"],
      },
    ],
  },
  "pull-day": {
    title: "X3 Pull Day",
    description:
      "Targets the muscles responsible for pulling movements, emphasizing your back, biceps, and posterior chain.",
    exercises: [
      { ...twelveWeekExercises["deadlift"], slug: "deadlift" },
      { ...twelveWeekExercises["bent-over-row"], slug: "bent-over-row" },
      { ...twelveWeekExercises["bicep-curl"], slug: "bicep-curl" },
      { ...twelveWeekExercises["calf-raise"], slug: "calf-raise" },
    ],
  },
};
